<template>
  <div>
    <div
      class="flex flex-row justify-between items-center mb-4 bg-white shadow-sm sm:rounded-t-md py-6 px-4 sm:px-6 lg:px-8"
    >
      <div class="flex flex-col w-full sm:w-1/4">
        <DateRangePicker v-model="date" @update:model-value="updateDate" />
      </div>
      <SecondaryButton v-if="storesView || sellersView" @click="goBack">Précédent</SecondaryButton>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center">
      <Loading />
    </div>
    <div v-else>
      <CommonAnalyticsTable
        :stats="stats"
        :total="total"
        :currentMonth="currentMonth"
        :statsClickable="organizationsView || storesView"
        :showTargetColumns="!sellersView"
        @rowClicked="rowClicked"
      />
    </div>
  </div>
</template>

<script>
import { DateRangePicker, Loading, SecondaryButton, isObjectValid } from "@estaly/ui";
import { currentMonthRange } from "@/utils/date";
import CommonAnalyticsTable from "@/components/analytics/retail/utils/CommonAnalyticsTable.vue";
import { formatResponse } from "@/utils/backend_response_formatter";
import { formatAnalyticsDate } from "@/utils/date_formatter";

import { mapActions, mapState } from "vuex";

export default {
  components: {
    DateRangePicker,
    CommonAnalyticsTable,
    Loading,
    SecondaryButton,
  },

  mounted() {
    if (this.signedIn) {
      this.loadQueryParams();
      if (!this.$route.query.start) {
        // If no date is set, set the current month
        this.date = currentMonthRange();
        this.updateCurrentMonth(this.date[1]);
      }
      this.getAnalytics();
    } else {
      this.$router.push("/signin");
    }
  },

  data: () => {
    return {
      isLoading: false,
      organizationId: null,
      storeId: null,
      stats: null,
      total: null,
      date: currentMonthRange(),
      currentMonth: "",
    };
  },

  computed: {
    ...mapState("auth", ["signedIn"]),

    organizationsView() {
      return !this.storesView && !this.sellersView;
    },

    storesView() {
      return !this.sellersView && this.organizationId != null;
    },

    sellersView() {
      return this.storeId != null;
    },

    sinceBeginningStartDate() {
      return new Date(2022, 1, 1);
    },

    sinceBeginningEndDate() {
      return new Date();
    },
  },

  methods: {
    ...mapActions("analytics", {
      getAnalyticsOrganizations: "getOrganizations",
      getAnalyticsStores: "getStores",
      getAnalyticsSellers: "getSellers",
    }),
    isObjectValid,

    async getAnalytics() {
      this.isLoading = true;

      try {
        const start = Array.isArray(this.date) && this.date[0] ? formatAnalyticsDate(this.date[0]) : null;
        const end = Array.isArray(this.date) && this.date[1] ? formatAnalyticsDate(this.date[1]) : null;

        let response;
        if (this.sellersView) {
          response = await this.getAnalyticsSellers({ storeId: this.storeId, startDate: start, endDate: end });
        } else if (this.storesView) {
          response = await this.getAnalyticsStores({
            organizationId: this.organizationId,
            startDate: start,
            endDate: end,
          });
        } else {
          response = await this.getAnalyticsOrganizations({ startDate: start, endDate: end });
        }
        if (response?.success) {
          const formattedResponse = formatResponse(response.data);
          this.stats = formattedResponse.stats;
          this.total = formattedResponse.total;
        }
      } finally {
        this.isLoading = false;
      }
    },

    updateQueryParams() {
      const query = {};
      if (this.date && this.date.length === 2) {
        query.start = formatAnalyticsDate(this.date[0]);
        query.end = formatAnalyticsDate(this.date[1]);
      }
      if (this.organizationId) query.organizationId = this.organizationId;
      if (this.storeId) query.storeId = this.storeId;
      this.$router.push({ query });
    },

    loadQueryParams() {
      this.organizationId = this.$route.query.organizationId;
      this.storeId = this.$route.query.storeId;
      if (this.$route.query.start && this.$route.query.end) {
        this.date = [new Date(this.$route.query.start), new Date(this.$route.query.end)];
      } else {
        this.date = currentMonthRange();
      }
      if (this.date[0] && this.date[1]) this.updateCurrentMonth(this.date[1]);
    },

    goBack() {
      this.isLoading = true;
      if (this.sellersView) {
        this.storeId = null;
      } else if (this.storesView) {
        this.storeId = null;
        this.organizationId = null;
      }
      this.updateQueryParams();
    },

    rowClicked(row) {
      this.isLoading = true;
      if (this.organizationsView) {
        this.organizationId = row.id;
        this.storeId = null;
      } else if (this.storesView) {
        this.storeId = row.id;
      }
      this.updateQueryParams();
    },

    updateDate(date) {
      this.date = date;
      this.updateQueryParams();
    },

    updateCurrentMonth(endDate) {
      const monthNames = [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ];
      const month = monthNames[endDate.getMonth()];
      const year = endDate.getFullYear();
      this.currentMonth = `${month} ${year}`;
    },
  },

  watch: {
    "$route.query"() {
      this.loadQueryParams();
      this.getAnalytics();
    },
  },
};
</script>
